const VoyagerEnhancedSearchSchema = {
  title: 'Enhanced Search (AI Chat)',
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: [
        'headerTitle',
        'headerSubtitle',
        'greetingMessage',
        'projectInfoUrl',
        'projectInfoLinkText',
        'placeholder',
        'searchPagePath',
      ],
    },
    {
      id: 'api',
      title: 'API Configuration',
      fields: [
        'agentEndpoint',
        'selectedChatEndpoint',
        'verificationEndpoint',
        'feedbackEndpoint',
        'feedbackTagsPositive',
        'feedbackTagsNegative',
        'turnstileSiteKey',
        'allowLocalDevBypass',
        'searchUrl',
      ],
    },
    {
      id: 'display',
      title: 'Display Options',
      fields: [
        'feedbackStyle',
        'enableChart',
        'chartTitle',
        'fabPosition',
        'chatWithPdfButtonText',
        'showSessionId',
        'autoApplyGeneratedSearch',
        'showDebugInfo',
      ],
    },
  ],
  properties: {
    headerTitle: {
      title: 'Header Title',
      description: 'Title shown in the chat header.',
      type: 'string',
      default: 'Enhanced Search',
    },
    headerSubtitle: {
      title: 'Header Subtitle',
      description: 'Subtext shown beneath the title in the chat header.',
      type: 'string',
      default: 'Natural language to advanced search queries',
    },
    greetingMessage: {
      title: 'Greeting Message',
      description:
        'Opening message shown before the first question. Use it to welcome the user, explain the tool, and note that it is a prototype. Line breaks are preserved.',
      type: 'string',
      widget: 'textarea',
      default:
        'Hello! Welcome to AskNSDI.\n\nPrototype Disclaimer: This AI search tool is intended for testing and requirements gathering. Responses may be inaccurate and are not official. Please verify results with the authoritative source.\n\nWhat geospatial data are you looking for?',
    },
    projectInfoUrl: {
      title: 'Project Information Link',
      description:
        'Optional URL to additional project information. When set, a link is shown beneath the disclaimer/greeting. Supports internal paths or external URLs.',
      type: 'string',
    },
    projectInfoLinkText: {
      title: 'Project Information Link Text',
      description:
        'Text for the project information link shown beneath the disclaimer. Only used when a link is provided.',
      type: 'string',
      default: 'Learn more about this project',
    },
    placeholder: {
      title: 'Search Placeholder',
      description: 'Placeholder text for the chat input',
      type: 'string',
      default: 'What are you looking for?',
    },
    searchPagePath: {
      title: 'Search Page Path',
      description:
        'The path to navigate to when clicking a generated search URL. Supports internal paths (e.g., /nsdi-search) or external URLs (e.g., https://example.com/search).',
      type: 'string',
      default: '/voyager-search',
    },
    agentEndpoint: {
      title: 'Agent API Endpoint',
      description:
        'Optional URL for the AI agent endpoint. Leave empty to use mock responses.',
      type: 'string',
    },
    selectedChatEndpoint: {
      title: 'Selected Chat Endpoint',
      description: 'Choose which backend chat route to use for requests.',
      type: 'string',
      choices: [
        ['/chat2', '/chat2 (JSON)'],
        ['/chat3', '/chat3 (Streaming)'],
      ],
      default: '/chat2',
    },
    verificationEndpoint: {
      title: 'Verification API Endpoint',
      description:
        'Optional base URL used for CAPTCHA verification. The widget posts to /verify-token.',
      type: 'string',
    },
    feedbackEndpoint: {
      title: 'Feedback API Endpoint',
      description:
        'Optional full URL that receives response ratings via POST. When set, a 1-5 star rating with an optional comment appears under each agent response. Payload: { traceId?, sessionId?, rating, comment? }.',
      type: 'string',
      default: 'https://7u0cumuy79.execute-api.us-east-1.amazonaws.com/score',
    },
    feedbackTagsPositive: {
      title: 'Positive Feedback Tags',
      description:
        'Optional tags (e.g. accurate, helpful, relevant) shown as selectable chips when the user rates a response positively (thumbs up or a high star rating). The selected tags are sent as the "tags" array with the feedback.',
      type: 'array',
      items: {
        type: 'string',
      },
    },
    feedbackTagsNegative: {
      title: 'Negative Feedback Tags',
      description:
        'Optional tags (e.g. inaccurate, unhelpful, off-topic) shown as selectable chips when the user rates a response negatively (thumbs down or a low star rating). The selected tags are sent as the "tags" array with the feedback.',
      type: 'array',
      items: {
        type: 'string',
      },
    },
    turnstileSiteKey: {
      title: 'Turnstile Site Key',
      description:
        'Cloudflare Turnstile site key used by the verification widget.',
      type: 'string',
    },
    allowLocalDevBypass: {
      title: 'Allow Localhost CAPTCHA Bypass',
      description:
        'Dev only: allows skipping CAPTCHA when running on localhost/127.0.0.1 if Turnstile is unavailable.',
      type: 'boolean',
      default: false,
    },
    searchUrl: {
      title: 'Custom Solr Search URL',
      description:
        'Optional Solr endpoint used by the backend agent. Sent on the first request of each new session.',
      type: 'string',
    },
    feedbackStyle: {
      title: 'Rating Style',
      description:
        'How users rate a response. "Stars" shows a 1-5 star scale. "Thumbs" shows thumbs up/down, where thumbs up is submitted as 5 and thumbs down as 1.',
      type: 'string',
      choices: [
        ['stars', 'Star rating (1-5)'],
        ['thumbs', 'Thumbs up / down'],
      ],
      default: 'thumbs',
    },
    enableChart: {
      title: 'Enable Chart',
      description:
        'Display the horizontal bar chart visualization in responses',
      type: 'boolean',
      default: true,
    },
    chartTitle: {
      title: 'Chart Title',
      description: 'Title displayed above the results chart',
      type: 'string',
      default: 'Results Summary',
    },
    fabPosition: {
      title: 'Button Position',
      description: 'Position of the floating action button',
      type: 'string',
      choices: [
        ['bottom-right', 'Bottom Right'],
        ['bottom-left', 'Bottom Left'],
      ],
      default: 'bottom-right',
    },
    chatWithPdfButtonText: {
      title: 'Chat with PDF Button Text',
      description:
        'Text displayed on PDF chat buttons when this chat block is present on the page.',
      type: 'string',
      default: 'Chat with PDF',
    },
    showDebugInfo: {
      title: 'Show Debug Info',
      description:
        'Display local debug diagnostics in chat and verification panels.',
      type: 'boolean',
      default: false,
    },
    showSessionId: {
      title: 'Show Session ID',
      description: 'Display the current session ID below the chat header.',
      type: 'boolean',
      default: true,
    },
    autoApplyGeneratedSearch: {
      title: 'Auto Apply Generated Search',
      description:
        'Automatically update/navigate to the configured search page when the agent returns a generated search URL. Leave off when placing chat on informational pages.',
      type: 'boolean',
      default: false,
    },
  },
  required: [],
};

export default VoyagerEnhancedSearchSchema;
